import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { AppFeedbackResponse, AppFeedbackSummaryResponse } from "@/types/generated/cms.generated";

/**
 * Feedback about the employee app (CMS API `app-feedback`; AthlynAdmin + AthlynTeam, platform-wide).
 * Contains personal data (names, emails): never log it.
 */

/** App feedback created in the last `days` days (1–365), newest first, submitted or not. */
export function useAppFeedback(days: number) {
  return useQuery<AppFeedbackResponse[]>({
    queryKey: ["app-feedback", days],
    queryFn: () => apiRequest<AppFeedbackResponse[]>("GET", `/app-feedback?days=${days}`),
  });
}

/** Count and average rating (2 dp; null when none rated) over the last `days` days. */
export function useAppFeedbackSummary(days: number) {
  return useQuery<AppFeedbackSummaryResponse>({
    queryKey: ["app-feedback", "summary", days],
    queryFn: () => apiRequest<AppFeedbackSummaryResponse>("GET", `/app-feedback/summary?days=${days}`),
  });
}

/** The App feedback menu badge window. */
export const NEW_APP_FEEDBACK_DAYS = 7;

/**
 * App feedback received in the last 7 days — the App feedback menu badge. The always-mounted
 * sidebar runs this on every CMS page, so it is fresh for 60 s. `undefined` while loading/failed.
 */
export function useNewAppFeedbackCount(): number | undefined {
  const { data, isSuccess } = useQuery<AppFeedbackSummaryResponse>({
    queryKey: ["app-feedback", "summary", NEW_APP_FEEDBACK_DAYS],
    queryFn: () => apiRequest<AppFeedbackSummaryResponse>("GET", `/app-feedback/summary?days=${NEW_APP_FEEDBACK_DAYS}`),
    staleTime: 60 * 1000,
  });
  return isSuccess ? data?.count : undefined;
}
